import { apiClient, apiPostFormData } from "@/lib/api-client";
import type {
  UpdateProfileRequest,
  ChangePasswordRequest,
  UploadAvatarRequest,
  UploadAvatarResponse,
  UserEmailAccount,
  OAuthAuthorizationResponse,
  OAuthCallbackRequest,
} from "@/types/api/profile";
import type { UserInfo } from "@/types/api/auth";
import type { MessageResponse } from "@/types/api/auth";

/**
 * Update current user's profile
 */
export async function updateProfile(data: UpdateProfileRequest): Promise<UserInfo> {
  return apiClient<UserInfo>("/auth/me", {
    method: "PUT",
    body: JSON.stringify(data),
    requireAuth: true,
  });
}

/**
 * Change current user's password
 */
export async function changePassword(data: ChangePasswordRequest): Promise<MessageResponse> {
  return apiClient<MessageResponse>("/auth/change-password", {
    method: "POST",
    body: JSON.stringify(data),
    requireAuth: true,
  });
}

/**
 * Upload profile picture (avatar)
 */
export async function uploadAvatar(data: UploadAvatarRequest): Promise<UploadAvatarResponse> {
  const formData = new FormData();
  formData.append("file", data.file);
  if (data.company_id) {
    formData.append("company_id", data.company_id);
  }

  return apiPostFormData<UploadAvatarResponse>("/auth/upload-avatar", formData, {
    requireAuth: true,
  });
}

/**
 * Get list of user's email accounts
 */
export async function getUserEmailAccounts(): Promise<UserEmailAccount[]> {
  return apiClient<UserEmailAccount[]>("/user-email-accounts", {
    method: "GET",
    requireAuth: true,
  });
}

/**
 * Get a specific user email account
 */
export async function getUserEmailAccount(accountId: string): Promise<UserEmailAccount> {
  return apiClient<UserEmailAccount>(`/user-email-accounts/${accountId}`, {
    method: "GET",
    requireAuth: true,
  });
}

/**
 * Start OAuth flow for connecting email account
 */
export async function startEmailOAuthFlow(
  provider: "gmail" | "outlook",
  redirectUri: string
): Promise<OAuthAuthorizationResponse> {
  return apiClient<OAuthAuthorizationResponse>(
    `/user-email-accounts/authorize/${provider}?redirect_uri=${encodeURIComponent(redirectUri)}`,
    {
      method: "POST",
      requireAuth: true,
    }
  );
}

/**
 * Complete OAuth callback
 */
export async function completeEmailOAuthCallback(
  provider: "gmail" | "outlook",
  data: OAuthCallbackRequest,
  accountId: string
): Promise<UserEmailAccount> {
  return apiClient<UserEmailAccount>(
    `/user-email-accounts/callback/${provider}?account_id=${accountId}`,
    {
      method: "POST",
      body: JSON.stringify(data),
      requireAuth: true,
    }
  );
}

/**
 * Disconnect (delete) a user email account
 */
export async function disconnectEmailAccount(accountId: string): Promise<void> {
  return apiClient<void>(`/user-email-accounts/${accountId}`, {
    method: "DELETE",
    requireAuth: true,
  });
}

